/**
 * #1212 (Ted #40) — Settings → Texting: connect the business's OWN Twilio
 * account, see that it's connected, send yourself a test text.
 *
 * Ted opened his Twilio account on 2026-10-10; carrier registration takes
 * weeks. This card is how an owner plugs the account in today and proves the
 * wiring the day registration clears — and, until then, sees "your number
 * isn't approved by the carriers yet" instead of a mystery failure.
 *
 * Secret handling (mirrors the server, and the gateway-credentials card):
 *   - The Auth Token is WRITE-ONLY. The server answers SID last 4 + the
 *     sender, never the token, and this card never stores it in React
 *     state: the field is UNCONTROLLED (read through a ref on submit), so
 *     React never mirrors the value into a DOM attribute, and it is cleared
 *     the moment a save lands (AC16).
 *   - Connecting texts nobody; the card says so rather than imply a program
 *     that isn't live. TextingMessagesCard above it carries the live state.
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: Connected / Not connected is the badge; the
 *    status rows say which account (•••• last 4), which number, and when
 *    Twilio last accepted it.
 *  - #2 The next action is obvious: one primary per view — "Connect Twilio"
 *    when disconnected, "Send test text" when connected. Disconnect is a
 *    secondary, two-tap action that spells out the consequence.
 *  - #5 Never lie about data: a send that Twilio queued, or only handed to
 *    the carrier (`sent`), says "on its way", not "delivered"; an unregistered number says registration is still in
 *    progress; an unreadable saved connection says so and how to recover.
 *  - #7 Built for hands: every control is a full-size field/button; the
 *    phone keyboard opens for numbers (inputMode="tel").
 *  - Five states: loading (skeleton) · error (+Retry) · empty (not
 *    connected — teaching copy + the connect form) · ready (connected) ·
 *    saving/sending (in-flight locks with progress copy). Stale doesn't
 *    apply — the card reads live state on open.
 *
 * Counter Test (owner altitude): Ted pastes his Account SID and Auth Token,
 * types his Twilio number, taps Connect Twilio, sees "Connected · sends from
 * (303) 555-0101", types his cell, taps Send test text, and either his phone
 * buzzes or the card tells him registration isn't done yet. No script.
 *
 * Server contract: GET/PUT/DELETE /api/v2/admin/tenant/messaging,
 * POST /messaging/test — all payments-ops.credentials:write:all (owner).
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Alert, Badge, Button, Card, Input, Skeleton } from '@ferro/ui';
import {
  adminMessagingSettingsApi,
  type ApiResponse,
  type MessagingConnectInput,
  type MessagingSenderView,
  type MessagingSettings,
} from '../../lib/api.js';
import { TextingEscalationForm } from './TextingEscalationForm.js';

interface MessagingSettingsCardProps {
  apiUrl: string;
  /** payments-ops.credentials:write:all — the owner. */
  canManage: boolean;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; settings: MessagingSettings };

type SaveState = { kind: 'idle' } | { kind: 'saving' } | { kind: 'error'; message: string; field?: string };

type TestState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; status: string }
  | { kind: 'error'; message: string; code?: string; field?: string };

type DisconnectState = { kind: 'idle' } | { kind: 'confirming' } | { kind: 'working' } | { kind: 'error'; message: string };

type SenderKind = 'number' | 'messaging_service';

/** "+13035550101" → "(303) 555-0101"; anything else as given. */
export function formatUsPhone(e164: string): string {
  const m = e164.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

function describeSender(sender: MessagingSenderView | null): string {
  if (sender === null) return 'Unreadable — disconnect and connect again';
  return sender.kind === 'number' ? formatUsPhone(sender.e164) : `Messaging Service •••• ${sender.sidLast4}`;
}

function formatVerified(iso: string | null): string {
  if (!iso) return 'Not recorded';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? 'Not recorded'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** The server's 400 names the field; surface its sentence on that field. */
function validationDetail(res: ApiResponse<unknown>): { field?: string; message?: string } {
  const details = res.details;
  if (!Array.isArray(details) || details.length === 0) return {};
  const first = details[0] as { field?: unknown; message?: unknown };
  return {
    field: typeof first.field === 'string' ? first.field : undefined,
    message: typeof first.message === 'string' ? first.message : undefined,
  };
}

/**
 * Only `delivered`/`read` mean the text reached the phone. Twilio's `sent`
 * is "handed to the carrier" — the carrier can still refuse it (an
 * unverified toll-free number does, seconds later) — so `sent`, `queued`
 * and `sending` all read as on-its-way, never as arrived.
 */
function sentCopy(status: string): string {
  return status === 'delivered' || status === 'read'
    ? 'Delivered. It reached that phone.'
    : 'Handed to Twilio — it’s on its way. If it doesn’t arrive in a minute or two, your number may still be waiting on carrier approval.';
}

export function MessagingSettingsCard({ apiUrl, canManage }: MessagingSettingsCardProps): JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  const [test, setTest] = useState<TestState>({ kind: 'idle' });
  const [disconnect, setDisconnect] = useState<DisconnectState>({ kind: 'idle' });
  const [accountSid, setAccountSid] = useState('');
  const [senderKind, setSenderKind] = useState<SenderKind>('number');
  const [senderNumber, setSenderNumber] = useState('');
  const [serviceSid, setServiceSid] = useState('');
  const [testTo, setTestTo] = useState('');
  // Write-only secret: never in React state (see header).
  const tokenRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    setLoad({ kind: 'loading' });
    const res = await adminMessagingSettingsApi.get(apiUrl);
    if (res.error || !res.data) {
      setLoad({ kind: 'error', message: res.error ?? 'Could not load the texting settings.' });
      return;
    }
    setLoad({ kind: 'ready', settings: res.data });
  }, [apiUrl]);

  useEffect(() => {
    if (canManage) void refresh();
  }, [canManage, refresh]);

  const handleConnect = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const authToken = tokenRef.current?.value ?? '';
      const input: MessagingConnectInput = {
        accountSid: accountSid.trim(),
        authToken: authToken.trim(),
        sender:
          senderKind === 'number'
            ? { kind: 'number', e164: senderNumber.trim() }
            : { kind: 'messaging_service', sid: serviceSid.trim() },
      };
      setSave({ kind: 'saving' });
      const res = await adminMessagingSettingsApi.connect(apiUrl, input);
      if (res.error || !res.data) {
        const detail = validationDetail(res);
        setSave({ kind: 'error', message: detail.message ?? res.error ?? 'Could not connect.', field: detail.field });
        return;
      }
      // The seal landed: wipe the secret and the form.
      if (tokenRef.current) tokenRef.current.value = '';
      setAccountSid('');
      setSenderNumber('');
      setServiceSid('');
      setSave({ kind: 'idle' });
      setTest({ kind: 'idle' });
      setLoad({ kind: 'ready', settings: res.data });
    },
    [apiUrl, accountSid, senderKind, senderNumber, serviceSid],
  );

  const handleTest = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setTest({ kind: 'sending' });
      const res = await adminMessagingSettingsApi.sendTestText(apiUrl, testTo.trim());
      if (res.error || !res.data) {
        const detail = validationDetail(res);
        setTest({
          kind: 'error',
          message: detail.message ?? res.error ?? 'The test text did not go out.',
          code: res.code,
          field: detail.field,
        });
        return;
      }
      setTest({ kind: 'sent', status: res.data.message.status });
    },
    [apiUrl, testTo],
  );

  const handleDisconnect = useCallback(async () => {
    setDisconnect({ kind: 'working' });
    const res = await adminMessagingSettingsApi.disconnect(apiUrl);
    if (res.error || !res.data) {
      setDisconnect({ kind: 'error', message: res.error ?? 'Could not disconnect.' });
      return;
    }
    setDisconnect({ kind: 'idle' });
    setTest({ kind: 'idle' });
    setLoad({ kind: 'ready', settings: res.data });
  }, [apiUrl]);

  const badge =
    load.kind === 'ready' ? (
      load.settings.connected ? (
        <Badge variant="success">Connected</Badge>
      ) : (
        <Badge variant="neutral">Not connected</Badge>
      )
    ) : null;

  return (
    <Card title="Texting (Twilio)" headerRight={<span data-testid="messaging-badge">{badge}</span>}>
      <div className="ferro-settings__card" data-testid="messaging-card">
        {!canManage ? (
          <p className="ferro-settings__teaching" data-testid="messaging-owner-only">
            Only the business owner can connect or change the texting account.
          </p>
        ) : null}

        {canManage && load.kind === 'loading' ? (
          <div role="status" aria-label="Loading texting settings">
            <Skeleton variant="text" lines={4} />
          </div>
        ) : null}

        {canManage && load.kind === 'error' ? (
          <Alert variant="error">
            <p>{load.message}</p>
            <Button type="button" variant="ghost" onClick={() => void refresh()}>
              Retry
            </Button>
          </Alert>
        ) : null}

        {canManage && load.kind === 'ready' && !load.settings.connected ? (
          <>
            <p className="ferro-settings__teaching">
              Connect the Twilio account your business texts from. You’ll find the Account SID and Auth Token on
              your Twilio Console home page, and your number under Phone Numbers → Active numbers. We check them
              with Twilio before saving; the Auth Token is encrypted and never shown again.
            </p>
            <p className="ferro-settings__footnote ferro-muted">
              Connecting doesn’t text your clients. The card above shows whether texting is live and what’s left.
            </p>
            <form
              className="ferro-settings__form ferro-settings__card"
              onSubmit={(e) => void handleConnect(e)}
              autoComplete="off"
              data-testid="messaging-connect-form"
            >
              <Input
                id="messaging-account-sid"
                label="Account SID"
                placeholder="e.g. AC1234…"
                value={accountSid}
                onChange={(e) => setAccountSid(e.target.value)}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                error={save.kind === 'error' && save.field === 'accountSid' ? save.message : undefined}
                data-testid="messaging-account-sid"
              />
              <Input
                ref={tokenRef}
                id="messaging-auth-token"
                label="Auth Token"
                type="password"
                defaultValue=""
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                data-1p-ignore=""
                data-lpignore="true"
                helperText="Write-only: it is encrypted on save and never shown again."
                error={save.kind === 'error' && save.field === 'authToken' ? save.message : undefined}
                data-testid="messaging-auth-token"
              />
              <fieldset className="ferro-settings__fieldset">
                <legend className="ferro-settings__label">Send texts from</legend>
                <label className="ferro-settings__field ferro-settings__radio">
                  <input
                    type="radio"
                    name="messaging-sender-kind"
                    checked={senderKind === 'number'}
                    onChange={() => setSenderKind('number')}
                    data-testid="messaging-sender-kind-number"
                  />
                  <span>A phone number on this Twilio account</span>
                </label>
                <label className="ferro-settings__field ferro-settings__radio">
                  <input
                    type="radio"
                    name="messaging-sender-kind"
                    checked={senderKind === 'messaging_service'}
                    onChange={() => setSenderKind('messaging_service')}
                    data-testid="messaging-sender-kind-service"
                  />
                  <span>A Messaging Service (if Twilio set one up for your registration)</span>
                </label>
              </fieldset>
              {senderKind === 'number' ? (
                <Input
                  id="messaging-sender-number"
                  label="Twilio phone number"
                  placeholder="e.g. (303) 555-0101"
                  type="tel"
                  inputMode="tel"
                  value={senderNumber}
                  onChange={(e) => setSenderNumber(e.target.value)}
                  error={save.kind === 'error' && save.field?.startsWith('sender') ? save.message : undefined}
                  data-testid="messaging-sender-number"
                />
              ) : (
                <Input
                  id="messaging-service-sid"
                  label="Messaging Service SID"
                  placeholder="e.g. MG1234…"
                  value={serviceSid}
                  onChange={(e) => setServiceSid(e.target.value)}
                  autoCapitalize="none"
                  spellCheck={false}
                  error={save.kind === 'error' && save.field?.startsWith('sender') ? save.message : undefined}
                  data-testid="messaging-service-sid"
                />
              )}
              {save.kind === 'error' && save.field === undefined ? (
                <Alert variant="error">
                  <span data-testid="messaging-connect-error">{save.message}</span>
                </Alert>
              ) : null}
              <div className="ferro-settings__actions">
                <Button type="submit" variant="primary" isLoading={save.kind === 'saving'} data-testid="messaging-connect">
                  {save.kind === 'saving' ? 'Checking with Twilio…' : 'Connect Twilio'}
                </Button>
              </div>
            </form>
          </>
        ) : null}

        {canManage && load.kind === 'ready' && load.settings.connected ? (
          <>
            <dl className="ferro-settings__readonly" data-testid="messaging-status">
              <div className="ferro-settings__readonly-row">
                <dt>Twilio account</dt>
                <dd data-testid="messaging-account-last4">
                  {load.settings.accountSidLast4 ? `•••• ${load.settings.accountSidLast4}` : 'Unreadable'}
                </dd>
              </div>
              <div className="ferro-settings__readonly-row">
                <dt>Texts come from</dt>
                <dd data-testid="messaging-sender">{describeSender(load.settings.sender)}</dd>
              </div>
              <div className="ferro-settings__readonly-row">
                <dt>Checked with Twilio</dt>
                <dd data-testid="messaging-verified">{formatVerified(load.settings.verifiedAt)}</dd>
              </div>
              <div className="ferro-settings__readonly-row">
                <dt>Auth Token</dt>
                <dd className="ferro-muted">Saved — write-only</dd>
              </div>
            </dl>
            <p className="ferro-settings__footnote ferro-muted">
              Connecting doesn’t text your clients. The card above shows whether texting is live and what’s left.
            </p>

            <form
              className="ferro-settings__form ferro-settings__card"
              onSubmit={(e) => void handleTest(e)}
              data-testid="messaging-test-form"
            >
              <h3 className="ferro-settings__subheading">Send yourself a test text</h3>
              <Input
                id="messaging-test-to"
                label="Your mobile number"
                placeholder="e.g. (720) 555-0100"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={testTo}
                onChange={(e) => {
                  setTestTo(e.target.value);
                  if (test.kind !== 'sending') setTest({ kind: 'idle' });
                }}
                error={test.kind === 'error' && test.field === 'to' ? test.message : undefined}
                data-testid="messaging-test-to"
              />
              {test.kind === 'sent' ? (
                <Alert variant="success">
                  <span data-testid="messaging-test-sent">{sentCopy(test.status)}</span>
                </Alert>
              ) : null}
              {test.kind === 'error' && test.field === undefined ? (
                <Alert variant={test.code === 'unregistered_sender' ? 'warning' : 'error'}>
                  <span data-testid="messaging-test-error" data-code={test.code ?? ''}>
                    {test.message}
                  </span>
                </Alert>
              ) : null}
              <div className="ferro-settings__actions">
                <Button type="submit" variant="primary" isLoading={test.kind === 'sending'} data-testid="messaging-test-send">
                  {test.kind === 'sending' ? 'Sending…' : 'Send test text'}
                </Button>
              </div>
            </form>

            <TextingEscalationForm apiUrl={apiUrl} />

            <div className="ferro-settings__card" data-testid="messaging-disconnect-area">
              {disconnect.kind === 'confirming' || disconnect.kind === 'working' ? (
                <Alert variant="warning">
                  <p>
                    Disconnect texting? The saved Twilio details are erased here (not in Twilio), and test texts
                    stop until you connect again.
                  </p>
                  <div className="ferro-settings__secondary-actions">
                    <Button
                      type="button"
                      variant="danger"
                      isLoading={disconnect.kind === 'working'}
                      onClick={() => void handleDisconnect()}
                      data-testid="messaging-disconnect-confirm"
                    >
                      Yes, disconnect
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setDisconnect({ kind: 'idle' })}>
                      Keep connected
                    </Button>
                  </div>
                </Alert>
              ) : (
                <div className="ferro-settings__secondary-actions">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDisconnect({ kind: 'confirming' })}
                    data-testid="messaging-disconnect"
                  >
                    Disconnect
                  </Button>
                </div>
              )}
              {disconnect.kind === 'error' ? <Alert variant="error">{disconnect.message}</Alert> : null}
            </div>
          </>
        ) : null}
      </div>
    </Card>
  );
}
